import React, {
	createContext,
	useContext,
	useEffect,
	useState,
	useCallback,
	useRef,
	type ReactNode,
	useMemo,
} from 'react'
import { type IActivity } from '#app/models/activity._._definitions.ts'
import { type IChatMember } from '#app/models/chat-member._._definitions.ts'
import { type IChat } from '#app/models/chat._._definitions.ts'
import { mapChatFields } from '#app/models/chat._._utils.ts'
import { type IMessage } from '#app/models/message._._definitions.ts'
import { mapMessageFields } from '#app/models/message._._utils.ts'
import { type IUser } from '#app/models/user._._definitions.ts'
import { createLogger } from '#app/utils/logger.ts'
import {
	type IServerSentEntity,
	type ISseStream,
	SSE_STREAM,
} from '#app/utils/sse.ts'

const sseLogger = createLogger('SseProvider', true, 'debug')

/**
 * @fileOverview SseProvider Component
 *
 * @description
 * This component provides a centralized context for managing Server-Sent Event (SSE)
 * connections within the application. It aims to:
 * - Establish and maintain a single EventSource connection (per SSE URL) for logged-in users.
 * - Receive events from the server via this connection.
 * - Parse, filter, and distribute event data to interested components.
 * - Provide mechanisms for components to subscribe to specific event types using the `useSseEvent` hook.
 * - Offer an explicit function (`disconnectAllSse`) to manually close all active SSE connections,
 *   primarily intended for use during service worker updates or explicit user actions.
 * - Automatically handle connection cleanup on user logout or component unmount.
 *
 * @usage
 * 1. Wrap the relevant part of your application (e.g., in `root.tsx` within `AppWithProviders`)
 *    with `<SseProvider>`.
 *    ```tsx
 *    <SseProvider>
 *      <App />
 *    </SseProvider>
 *    ```
 * 2. In components that need to react to SSE events, use the `useSseEvent` hook:
 *    ```tsx
 *    import { useSseEvent, SSE_STREAM } from '#app/components/sse-provider.tsx';
 *    import { type IMessage } from '#app/models/message._._definitions.ts';
 *
 *    function ChatMessages({ chatId }: { chatId: string }) {
 *      const newMessage = useSseEvent<IMessage>(SSE_STREAM.NEW_CHAT_MESSAGE, { chatId });
 *
 *      useEffect(() => {
 *        if (newMessage) {
 *          // Add the new message to your local state
 *        }
 *      }, [newMessage]);
 *
 *      // ... render messages
 *    }
 *    ```
 * 3. To manually disconnect all SSE connections (e.g., before a service worker update):
 *    ```tsx
 *    import { useSse } from '#app/components/sse-provider.tsx';
 *
 *    function MyUpdateComponent() {
 *      const { disconnectAllSse } = useSse();
 *
 *      const handleUpdateClick = async () => {
 *        sseLogger.log('Disconnecting SSE before SW update...');
 *        disconnectAllSse({
 *           onSuccess: () => sseLogger.log('SSE Disconnected Successfully'),
 *           onError: (err) => sseLogger.error('SSE Disconnect Error:', err),
 *        });
 *        // Add a small delay if needed, then proceed with SW update logic
 *        await new Promise(resolve => setTimeout(resolve, 150));
 *        // ... postMessage('SKIP_WAITING') etc.
 *      };
 *      // ...
 *    }
 *    ```
 */

// --- Utility Functions (from use-sse.ts) ---

export const parseServerSentEvent = (event: string | null) => {
	return event ? JSON.parse(event) : null
}

const getChatData = ({
	chat,
	options,
}: {
	chat: IChat
	options?: Record<string, any>
}): IChat | null => {
	if (options?.userId) {
		return chat?.members?.find((m) => m.userId === options.userId) ? chat : null
	}
	return chat
}

const getMessageData = ({
	message,
	options,
}: {
	message: IMessage
	options?: Record<string, any>
}): IMessage | null => {
	if (options?.chatId) {
		return message?.chatId === options.chatId ? message : null
	}
	return message
}

const getChatMemberData = ({
	member,
	options,
}: {
	member: IChatMember
	options?: Record<string, any>
}): IChatMember | null => {
	if (options?.userId) {
		return member?.userId === options.userId ? member : null
	}
	return member
}

const getActivityData = ({
	activity,
	options,
}: {
	activity: IActivity
	options?: Record<string, any>
}): IActivity | null => {
	if (options?.userId) {
		return activity?.users?.find((u) => u.userId === options.userId)
			? activity
			: null
	}
	return activity
}

// --- Context Definition ---

interface SseContextType {
	// Function to subscribe to a specific SSE event type
	subscribeToSseEvent: <T extends IServerSentEntity>(
		event: ISseStream,
		callback: (data: T | null) => void,
		options?: Record<string, any>, // Options like userId, chatId for filtering
	) => () => void // Returns an unsubscribe function
	// Function to manually disconnect all active SSE connections
	disconnectAllSse: (options?: {
		onSuccess?: () => void
		onError?: (error: unknown) => void
		permanent?: boolean // Added option
	}) => void // Updated signature
	// Optional: Expose permanent disconnect state if needed elsewhere
	// isPermanentlyDisconnected: boolean;
}

const SseContext = createContext<SseContextType | null>(null)

// --- Provider Component ---

interface SseProviderProps {
	children: ReactNode
	sseUrl?: string // Optional: Allow overriding the default URL
	userId: IUser['id'] | null // Accept userId as a prop
}

// Type for storing the actual handler function attached to EventSource
type EventHandler = (event: MessageEvent) => void

// Structure to hold listener info including the handler reference
interface ListenerInfo {
	callback: (data: any | null) => void
	options?: Record<string, any>
}

// Map from event type to the actual handler function attached to EventSource
type EventHandlersMap = Map<ISseStream, EventHandler>
// Map from event type to the set of component listeners
type ComponentListenersMap = Map<ISseStream, Set<ListenerInfo>>

// Revised structure for active sources
type ActiveEventSources = Map<
	string,
	{
		source: EventSource
		handlers: EventHandlersMap // Stores the actual functions passed to addEventListener
		listeners: ComponentListenersMap // Stores component callbacks & options
		errorRetryCount: number // Added retry counter
	}
>

// Define a retry threshold near the top or as a constant
const MAX_ERROR_RETRIES = 5

export function SseProvider({
	children,
	sseUrl = '/resources/sse', // Default SSE endpoint
	userId,
}: SseProviderProps) {
	const activeSourcesRef = useRef<ActiveEventSources>(new Map())
	const latestEventDataRef = useRef<Map<ISseStream, string | null>>(new Map())
	// State to block new connections after a permanent disconnect request
	const [isPermanentlyDisconnected, setIsPermanentlyDisconnected] =
		useState(false)

	// Log initialization only once on mount
	useEffect(() => {
		sseLogger.log('🐍 SseProvider: Initializing...', {})
	}, [])

	// Function to handle incoming messages for a specific event type
	// This function remains largely the same, but is called by the stored handlers
	const handleIncomingEvent = useCallback(
		(sseUrl: string, eventType: ISseStream, rawData: string | null) => {
			// Store the latest raw event data for this event type, so new subscribers can get the most recent event immediately
			latestEventDataRef.current.set(eventType, rawData)

			// Get the active EventSource entry for this SSE URL
			const sourceEntry = activeSourcesRef.current.get(sseUrl)
			if (!sourceEntry) return // If no source entry exists, exit early (shouldn't happen in normal flow)

			// Get the set of all component listeners (callbacks) for this event type
			const listenerEntries = sourceEntry.listeners.get(eventType)
			if (!listenerEntries) return // If no listeners for this event type, exit early

			// Parse the raw event data string into a usable JS object (or null if no data)
			const parsedData = rawData ? parseServerSentEvent(rawData) : null

			// For each registered listener for this event type, process and deliver the event
			listenerEntries.forEach(({ callback, options }) => {
				try {
					let filteredData: IServerSentEntity | null = null
					// Apply filtering based on event type and options
					if (parsedData) {
						switch (eventType) {
							case SSE_STREAM.NEW_CHAT:
								filteredData = getChatData({
									chat: mapChatFields(parsedData as IChat),
									options,
								})
								break
							case SSE_STREAM.NEW_CHAT_MESSAGE:
								filteredData = getMessageData({
									message: mapMessageFields(parsedData as IMessage),
									options,
								})
								break
							case SSE_STREAM.NEW_CHAT_MEMBER:
								filteredData = getChatMemberData({
									member: parsedData as IChatMember,
									options,
								})
								break
							case SSE_STREAM.NEW_ACTIVITY:
								filteredData = getActivityData({
									activity: parsedData as IActivity,
									options,
								})
								break
							// Add other event types here...
							default:
								// No specific filtering for other types yet
								filteredData = parsedData as IServerSentEntity | null
						}
					}
					// Ensure callback receives the correctly typed data or null
					// Only call callback if data is relevant after filtering OR if the original data was null
					if (filteredData !== null || rawData === null) {
						try {
							// sseLogger.debug(`SSE Provider: Delivering event ${eventType} to callback.`, filteredData); // DEBUG: Verbose logging
							callback(filteredData as any) // Cast needed? Type T handled by useSseEvent hook.
						} catch (error) {
							sseLogger.error(
								`SSE Provider: Error delivering event ${eventType} to callback: `,
								{ error },
							)
						}
					} else if (filteredData === null && parsedData !== null) {
						// Do nothing if filtered out
						// sseLogger.debug(`SSE Provider: Event ${eventType} filtered out for options:`, options); // DEBUG: Verbose logging
					}
				} catch (error) {
					sseLogger.error(
						`SSE Provider: Error processing event ${eventType} for callback:`,
						{ error },
					)
				}
			})
		},
		[],
	)

	// Function for components to subscribe to events
	const subscribeToSseEvent = useCallback(
		<T extends IServerSentEntity>(
			eventType: ISseStream,
			callback: (data: T | null) => void,
			options?: Record<string, any>,
		): (() => void) => {
			if (!userId) {
				sseLogger.warn(
					'SseProvider: Attempted to subscribe while not logged in.',
					{ eventType, callback, options },
				)
				return () => {}
			}

			// Prevent new connections if permanently disconnected
			if (isPermanentlyDisconnected) {
				sseLogger.warn(
					`SseProvider: Subscription attempt for ${eventType} blocked due to permanent disconnect.`,
					{ eventType, callback, options },
				)
				return () => {} // Return no-op unsubscribe
			}

			// sseLogger.debug(`SseProvider: Received subscription request for ${eventType} with options:`, options); // DEBUG

			let sourceEntry = activeSourcesRef.current.get(sseUrl)

			// Create EventSource if it doesn't exist for this URL
			if (!sourceEntry) {
				sseLogger.log(
					`SSE Provider: Establishing new EventSource connection to ${sseUrl}`,
					{ eventType, callback, options },
				)
				const source = new EventSource(sseUrl, { withCredentials: true })

				// Initialize with empty maps and zero retries
				sourceEntry = {
					source,
					handlers: new Map(),
					listeners: new Map(),
					errorRetryCount: 0, // Initialize retry count
				}
				activeSourcesRef.current.set(sseUrl, sourceEntry)

				// Use a variable for the entry inside handlers to ensure closure captures the right one
				// const currentSourceEntry = sourceEntry; // This variable isn't used below, can be removed if truly unused

				source.onopen = () => {
					sseLogger.log(
						`SSE Provider: EventSource connection opened successfully for ${sseUrl}`,
						{ eventType, callback, options },
					)
					// Reset retry count on successful connection
					// Check if entry still exists, as it might have been removed due to errors/cleanup
					const currentEntry = activeSourcesRef.current.get(sseUrl)
					if (currentEntry) {
						currentEntry.errorRetryCount = 0
					}
				}

				source.onerror = (error) => {
					// Note: EventSource automatically tries to reconnect on most errors.
					sseLogger.error(`SSE Provider: EventSource error for ${sseUrl}.`, {
						error,
					})
					const entry = activeSourcesRef.current.get(sseUrl)
					if (entry) {
						entry.errorRetryCount += 1
						sseLogger.warn(
							`SSE Provider: Incrementing error count for ${sseUrl} to ${entry.errorRetryCount}.`,
							{ error },
						)
						if (entry.errorRetryCount >= MAX_ERROR_RETRIES) {
							sseLogger.error(
								`SSE Provider: Max retries (${MAX_ERROR_RETRIES}) reached for ${sseUrl}. Closing connection permanently.`,
								{ error },
							)
							entry.source.close() // Close the faulty source
							activeSourcesRef.current.delete(sseUrl) // Remove from tracking
							// Optionally clear latest data for events associated ONLY with this source?
							// Maybe not necessary, as new connections won't use stale data.
						}
						// Otherwise, let the browser attempt reconnection as usual.
					} else {
						sseLogger.warn(
							`SSE Provider: EventSource error occurred, but no entry found for ${sseUrl} in active sources (might have been cleaned up already).`,
							{ error },
						)
					}
				}
			} else {
				// sseLogger.debug(`SSE Provider: Reusing existing EventSource for ${sseUrl}`); // DEBUG
			}

			// Get the current state of the source entry
			const { source, handlers, listeners } = sourceEntry

			// Add listener and handler for the specific event type if not already added
			if (!handlers.has(eventType)) {
				// sseLogger.debug( // DEBUG
				// 	`SSE Provider: Adding EventSource listener for event type: ${eventType}`,
				// );
				// Create the handler function that will be attached
				const handler: EventHandler = (event: MessageEvent) => {
					// sseLogger.debug(`SSE Provider: Received raw message for ${eventType}:`, event.data); // DEBUG: Raw message
					handleIncomingEvent(sseUrl, eventType, event.data || null)
				}
				handlers.set(eventType, handler) // Store the handler reference
				listeners.set(eventType, new Set()) // Initialize the listeners set for this event type
				source.addEventListener(eventType, handler) // Attach the specific handler
			}

			// Register the component's callback info
			const listenerSet = listeners.get(eventType)!
			const listenerEntry: ListenerInfo = { callback, options }
			listenerSet.add(listenerEntry)
			// sseLogger.debug(`SSE Provider: Registered listener for ${eventType}. Total listeners for this event: ${listenerSet.size}`); // DEBUG

			// --- Immediately provide the latest known value ---
			const latestRawData = latestEventDataRef.current.get(eventType)
			if (latestRawData !== undefined) {
				// sseLogger.debug(`SSE Provider: Providing latest known value for ${eventType} on subscribe:`, latestRawData); // DEBUG
				const parsedData = latestRawData
					? parseServerSentEvent(latestRawData)
					: null
				let filteredData: IServerSentEntity | null = null
				if (parsedData) {
					// Apply filtering (same logic as handleIncomingEvent)
					switch (eventType) {
						case SSE_STREAM.NEW_CHAT:
							filteredData = getChatData({
								chat: mapChatFields(parsedData as IChat),
								options,
							})
							break
						case SSE_STREAM.NEW_CHAT_MESSAGE:
							filteredData = getMessageData({
								message: mapMessageFields(parsedData as IMessage),
								options,
							})
							break
						case SSE_STREAM.NEW_CHAT_MEMBER:
							filteredData = getChatMemberData({
								member: parsedData as IChatMember,
								options,
							})
							break
						case SSE_STREAM.NEW_ACTIVITY:
							filteredData = getActivityData({
								activity: parsedData as IActivity,
								options,
							})
							break
						default:
							// No specific filtering for other types yet
							filteredData = parsedData as IServerSentEntity | null
					}
				}
				if (filteredData !== null || !parsedData) {
					try {
						// sseLogger.debug(`SSE Provider: Calling initial callback for ${eventType} with:`, filteredData); // DEBUG
						callback(filteredData as T | null) // Provide initial data correctly typed
					} catch (error) {
						sseLogger.error(
							`SSE Provider: Error in initial callback for ${eventType}:`,
							{ error },
						)
					}
				}
			} else {
				// sseLogger.debug(`SSE Provider: No previous value for ${eventType} to provide on subscribe.`); // DEBUG
			}
			// --- End Immediate Provide ---

			// Return unsubscribe function
			return () => {
				// sseLogger.debug(`SSE Provider: Unsubscribing listener from ${eventType}`); // DEBUG
				const currentSourceEntry = activeSourcesRef.current.get(sseUrl)
				if (!currentSourceEntry) {
					// sseLogger.warn(`SSE Provider: Attempted to unsubscribe from ${eventType}, but source for ${sseUrl} no longer exists.`); // DEBUG
					return
				}

				const currentHandlers = currentSourceEntry.handlers
				const currentListenersMap = currentSourceEntry.listeners
				const currentListenerSet = currentListenersMap.get(eventType)

				if (currentListenerSet) {
					currentListenerSet.delete(listenerEntry) // Remove the component's listener info
					// if (deleted) { // DEBUG
					// 	sseLogger.debug(`SSE Provider: Removed listener for ${eventType}. Remaining listeners for event: ${currentListenerSet.size}`);
					// } else {
					// 	sseLogger.warn(`SSE Provider: Listener for ${eventType} was not found in the set during unsubscribe.`);
					// }

					// If no component listeners left for this specific event type, remove the actual EventSource listener
					if (currentListenerSet.size === 0) {
						const handlerToRemove = currentHandlers.get(eventType)
						if (handlerToRemove) {
							// sseLogger.debug( // DEBUG
							// 	`SSE Provider: Removing EventSource listener for event type: ${eventType} as no components are listening.`,
							// );
							currentSourceEntry.source.removeEventListener(
								eventType,
								handlerToRemove, // Use the stored handler reference
							)
							currentHandlers.delete(eventType) // Remove handler reference
							currentListenersMap.delete(eventType) // Remove the Set for this event type
							latestEventDataRef.current.delete(eventType) // Clean up last known data
							// sseLogger.debug(`SSE Provider: Cleaned up handler and listener map for ${eventType}.`); // DEBUG
						} else {
							// sseLogger.warn(`SSE Provider: No handler found for ${eventType} during cleanup, though listener set was empty.`); // DEBUG
						}
					}

					// Check if *any* listeners remain across all event types for this source
					let totalListeners = 0
					currentListenersMap.forEach((listenerSet) => {
						totalListeners += listenerSet.size
					})
					// sseLogger.debug(`SSE Provider: Total remaining listeners across all events for ${sseUrl}: ${totalListeners}`); // DEBUG

					// If no listeners left for any event type on this source, close the source
					if (totalListeners === 0) {
						sseLogger.log(
							`SSE Provider: Closing EventSource for ${sseUrl} as no listeners remain across any event type.`,
							{ eventType, callback, options },
						)
						currentSourceEntry.source.close()
						activeSourcesRef.current.delete(sseUrl)
						// sseLogger.debug(`SSE Provider: Removed source entry for ${sseUrl}.`); // DEBUG
					}
				} else {
					// sseLogger.warn(`SSE Provider: Listener set for ${eventType} not found during unsubscribe.`); // DEBUG
				}
			}
		},
		[userId, sseUrl, handleIncomingEvent, isPermanentlyDisconnected], // Added isPermanentlyDisconnected
	)

	const disconnectAllSse = useCallback(
		(options?: {
			onSuccess?: () => void
			onError?: (error: unknown) => void
			permanent?: boolean
		}) => {
			const sourceCount = activeSourcesRef.current.size
			if (sourceCount === 0) {
				sseLogger.log(
					'SSE Provider: disconnectAllSse called, but no active connections found.',
					{ options },
				)
				options?.onSuccess?.()
				return
			}

			sseLogger.log(
				`SSE Provider: Attempting to disconnect ${sourceCount} SSE connection(s)...`,
				{ options },
			)
			try {
				let closedCount = 0
				activeSourcesRef.current.forEach(({ source }, url) => {
					try {
						sseLogger.log(`SSE Provider: Closing connection for ${url}`, {
							options,
						})
						source.close()
						closedCount++
					} catch (closeError) {
						sseLogger.error(
							`SSE Provider: Error closing connection for ${url}:`,
							{ closeError },
						)
						// Continue closing others even if one fails
					}
				})
				const deletedCount = activeSourcesRef.current.size // Store size before clearing
				activeSourcesRef.current.clear()
				latestEventDataRef.current.clear()
				sseLogger.log(
					`SSE Provider: Disconnect operation finished. Closed ${closedCount} connections, removed ${deletedCount} entries from tracking map. 🚪`,
					{ options },
				)
				options?.onSuccess?.() // Call onSuccess callback

				// Set permanent disconnect flag *after* cleanup if requested
				if (options?.permanent) {
					sseLogger.log('SseProvider: Setting permanent disconnect flag.', {
						options,
					})
					setIsPermanentlyDisconnected(true)
				}
			} catch (error) {
				sseLogger.error(
					'SSE Provider: Unexpected error during disconnectAllSse:',
					{ error },
				)
				options?.onError?.(error) // Call onError callback
			}
		},
		[], // Dependencies remain empty as it modifies state via setter
	)

	// Cleanup on unmount or when user logs out
	useEffect(() => {
		const isUserLoggedIn = !!userId
		// sseLogger.log(
		// 	`SSE Provider: User effect triggered. User logged in: ${isUserLoggedIn}`,
		// )

		// Reset permanent disconnect flag on login/logout changes
		setIsPermanentlyDisconnected(false)

		if (!isUserLoggedIn) {
			sseLogger.log(
				'SSE Provider: User logged out or userId became null, ensuring all SSE connections are closed.',
				{},
			)
			disconnectAllSse({
				onSuccess: () =>
					sseLogger.log(
						'SSE Provider: Cleanup successful after user logout/change.',
						{},
					),
				onError: (err) =>
					sseLogger.error(
						'SSE Provider: Error during cleanup after user logout/change:',
						{ err },
					),
			})
		}

		// Return cleanup function for component unmount
		// REMOVED - Cleanup is handled by userId change and individual unsubscribes
		/*
		return () => {
			sseLogger.log(
				'SSE Provider: Component unmounting, ensuring all SSE connections are closed.',
			)
			disconnectAllSse({
				onSuccess: () =>
					sseLogger.log('SSE Provider: Cleanup successful on component unmount.'),
				onError: (err) =>
					sseLogger.error(
						'SSE Provider: Error during cleanup on component unmount:',
						err,
					),
			})
		}
		*/
	}, [userId, disconnectAllSse]) // Dependency includes userId and disconnectAllSse

	// Memoize the context value
	const contextValue = useMemo(
		() => ({
			subscribeToSseEvent,
			disconnectAllSse,
		}),
		[subscribeToSseEvent, disconnectAllSse],
	)

	return (
		<SseContext.Provider value={contextValue}>{children}</SseContext.Provider>
	)
}

// --- Consumer Hook ---
/**
 * Provides access to SSE subscription and control functions.
 * Must be used within an SseProvider.
 */
export function useSse() {
	const context = useContext(SseContext)
	if (!context) {
		throw new Error('useSse must be used within an SseProvider')
	}
	return context
}

// --- Hook for Using SSE Data ---
// This replaces the old useServerSentEventData hook logic
export function useSseEvent<T extends IServerSentEntity>(
	event: ISseStream,
	options?: Record<string, any>, // Options like userId, chatId for filtering
): T | null {
	const { subscribeToSseEvent } = useSse()
	const [data, setData] = useState<T | null>(null)
	const stringifiedOptions = useMemo(() => JSON.stringify(options), [options]) // Memoize stringified options

	useEffect(() => {
		// sseLogger.debug(`useSseEvent Hook: Effect running for ${event} with options ${stringifiedOptions}`); // DEBUG
		const unsubscribe = subscribeToSseEvent<T>(
			event,
			(newData) => {
				// sseLogger.debug(`useSseEvent Hook: Received data for ${event}:`, newData); // DEBUG
				// Optimization: Only update state if the data has actually changed
				// This requires careful comparison, especially for objects.
				// Simple string comparison of JSONified data might work for basic cases.
				// if (JSON.stringify(newData) !== JSON.stringify(data)) {
				//   setData(newData);
				// }
				// For now, always update:
				setData(newData)
			},
			options, // Pass the original options object
		)

		return () => {
			// sseLogger.debug(`useSseEvent Hook: Unsubscribing from ${event}`); // DEBUG
			unsubscribe()
		}
		// Use stringifiedOptions in dependency array to correctly detect option changes
	}, [subscribeToSseEvent, event, stringifiedOptions, options]) // Include original options too if subscribeToSseEvent needs it

	return data
}

// Re-create specific hooks using useSseEvent
export const useNewChatSse = (userId: IUser['id']) => {
	const options = useMemo(() => ({ userId }), [userId])
	return useSseEvent<IChat>(SSE_STREAM.NEW_CHAT, options)
}

export const useNewChatMessageSse = (chatId: IChat['id']) => {
	const options = useMemo(() => ({ chatId }), [chatId])
	return useSseEvent<IMessage>(SSE_STREAM.NEW_CHAT_MESSAGE, options)
}

export const useNewActivitySse = (userId: IUser['id']) => {
	const options = useMemo(() => ({ userId }), [userId])
	return useSseEvent<IActivity>(SSE_STREAM.NEW_ACTIVITY, options)
}

// The listener removal logic is now refined.
